import { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { IncidentReportPreviewUI } from './IncidentReportPreviewUI';
import { IncidentPDFDownloadButton } from './IncidentPDFDownloadButton';
import '../Stylesheets/incident-report.scss';
import '../Stylesheets/incident-report-public.scss';
import { SERVER_URL } from '../../../ServerLink';

function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('token') || null;
}

function decodeTokenPayload(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function isTokenExpired(payload) {
  if (!payload?.exp) return false;
  return Date.now() / 1000 > payload.exp;
}

async function fetchReportByToken(token) {
  const payload = decodeTokenPayload(token);
  if (!payload?.reportId) throw new Error('Invalid token: missing reportId');

  const url = `${SERVER_URL}/v2/incident-reports/public/preview/?token=${encodeURIComponent(token)}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || `Failed to load report (${res.status})`);
  }

  const json = await res.json();
  return json.data;
}

const LoadingScreen = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f1f5f9 0%, #e8eff6 100%)',
    gap: 20,
  }}>
    <div style={{
      width: 64,
      height: 64,
      borderRadius: 18,
      background: 'linear-gradient(135deg, #163b5f, #3a6b98)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 28px rgba(22,59,95,0.3)',
    }}>
      <FeatherIcon icon="file-text" size={26} color="#fff" />
    </div>
    <Spinner
      style={{
        width: 28,
        height: 28,
        borderWidth: 3,
        color: '#163b5f',
      }}
    />
    <p style={{ color: '#6a8ca8', fontSize: 13.5, margin: 0, fontWeight: 500 }}>
      Loading incident report…
    </p>
  </div>
);

const ErrorScreen = ({ error, expired }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f1f5f9 0%, #e8eff6 100%)',
    padding: 24,
  }}>
    <div style={{
      background: '#fff',
      borderRadius: 20,
      padding: '44px 48px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
      textAlign: 'center',
      maxWidth: 420,
      width: '100%',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{
        width: 68,
        height: 68,
        borderRadius: '50%',
        background: expired ? '#fef3c7' : '#fee2e2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 22px',
      }}>
        <FeatherIcon
          icon={expired ? 'clock' : 'alert-circle'}
          size={30}
          color={expired ? '#d97706' : '#ef4444'}
        />
      </div>
      <h2 style={{
        color: '#1e293b',
        fontSize: 20,
        fontWeight: 800,
        marginBottom: 10,
        letterSpacing: '-0.3px',
      }}>
        {expired ? 'Link Expired' : 'Unable to Load Report'}
      </h2>
      <p style={{
        color: '#64748b',
        fontSize: 13.5,
        lineHeight: 1.7,
        margin: 0,
      }}>
        {error || 'An unknown error occurred. Please contact the property manager for a new link.'}
      </p>
    </div>
  </div>
);

const IncidentReportPublicPreviewPage = () => {
  const [token] = useState(() => getTokenFromUrl());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tokenPayload = decodeTokenPayload(token);
  const expired = isTokenExpired(tokenPayload);

  useEffect(() => {
    if (!token) {
      setError('No preview token found in the URL.');
      setLoading(false);
      return;
    }
    if (expired) {
      setError('This preview link has expired.');
      setLoading(false);
      return;
    }

    fetchReportByToken(token)
      .then(data => setReportData(data))
      .catch(err => setError(err.message || 'Failed to load the incident report.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <LoadingScreen />;
  if (error || !token) return <ErrorScreen error={error} expired={expired || error?.includes('expired')} />;

  const report = reportData?.report;

  const propertyTitle = report?.propertyDetails?.buildingName || null;
  const referenceId = report?.referenceId
    || (report?.sequenceNumber ? `IR-${String(report.sequenceNumber).padStart(5, '0')}` : null);
  const expiresAt = tokenPayload?.exp ? new Date(tokenPayload.exp * 1000).toISOString() : null;
  const serverCanViewInvestigation = reportData?.canViewInvestigation ?? true;

  const formikShim = {
    values: {
      ...report,
      notification: report?.notification || {},
      investigation: report?.investigation || {},
      notificationEmails: report?.notificationEmails || [],
    },
  };

  return (
    <div style={{ minHeight: '100vh', background: '#eaeff4' }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 200,
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 24px',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 8px rgba(22,59,95,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/images/rentdigicare-logo.svg"
            alt="RentDigiCare"
            style={{ height: 26 }}
          />
          <div style={{
            width: 1,
            height: 20,
            background: '#e2e8f0',
          }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#163b5f', letterSpacing: '0.01em' }}>
            Incident Report
          </span>
          {referenceId && (
            <span style={{
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 700,
              background: '#eef3f7',
              border: '1px solid #c2d5e3',
              borderRadius: 6,
              padding: '2px 8px',
              color: '#3a6b98',
            }}>
              {referenceId}
            </span>
          )}
        </div>

        <IncidentPDFDownloadButton
          reportId={report?._id}
          token={token}
          label="Download PDF"
        />
      </div>

      <IncidentReportPreviewUI
        formik={formikShim}
        token={token}
        propertyTitle={propertyTitle}
        referenceId={referenceId}
        expiresAt={expiresAt}
        serverCanViewInvestigation={serverCanViewInvestigation}
      />
    </div>
  );
};

export default IncidentReportPublicPreviewPage;